import config from '@iotv/config'
import { StandardProceessEnv, dbObjectMeta, getZombieInstanceFromPrimaryKey } from '@iotv/iotv-v3-types'
import { v5 } from 'uuid'

const { projectCode } = process.env as StandardProceessEnv

const debug = !process.env.AWS_SESSION_TOKEN && false

const {
  app: { namespace_uuid },
} = config

type GetApexObjectIdInput = {
  /** the objectTypeId of the apex object */
  type: string
}

/** returns predictable id `Id` for a single apex object of `type`,
 * note: uses full `projectCode`, not substringed to 4 chars - will be same for each environment
 * This is name *independent*, only one object of this type will be present in the system, based on the `sk`, `pk` schema
 */
export const getApexObjectId = ({ type }: GetApexObjectIdInput) => {
  const canonicalProjectApexObjectTypeId = `${projectCode}-Apex-${type}`
  const Id = v5(canonicalProjectApexObjectTypeId, namespace_uuid)
  debug && console.log(`With namespace_uuid: ${namespace_uuid}, Apex ${type} id:`, Id)
  return Id
}

type GetApexNamedObjectIdInput = GetApexObjectIdInput & {
  name: string
}

/** returns predictable id `Id` for an apex object of `type` with given `name`,
 * note: uses full `projectCode`, not substringed to 4 chars - will be same for each environment
 * This is name *dependent*, only one object will be present with this type and name in the system, based on the `sk`, `pk` schema
 */
export const getApexNamedObjectId = ({ type, name }: GetApexNamedObjectIdInput) => {
  const Id = v5(`${projectCode}-${type}-${name}`, namespace_uuid)
  debug && console.log(`Named Item ${name}, With namespace_uuid: ${namespace_uuid}, Apex ${type}, Id:`, Id)
  return Id
}

type GetNamedApexInstanceInput = {
  type: string
  name: string
  others?: Record<string, any>
}

/** returns predictable datastore object with name and other att, for an apex object of type with given name,
 * note: uses full projectCode not substringed to 4 chars, will be same for each environment
 * This is name dependent, only one object  will be presen with this type and name in the system, based on the sk, pk schema
 */
export const getNamedApexInstance = ({ type, name, others }: GetNamedApexInstanceInput) => {
  const Id = getApexNamedObjectId({ type, name })
  const metadata: dbObjectMeta = { protected: true }
  const instance = getZombieInstanceFromPrimaryKey(`${type}:${Id}`, { ...others, name, metadata })
  return instance
}

type GetRelatedObjectIdInput = GetApexNamedObjectIdInput & {
  /** the uuid of and outV object */
  parentUuid: string
  /**  */
}

/** returns predictable id Id for a inV related object of type with given name,
 * note: uses uuid of outV object,
 * This is name and parent dependent,
 */
export const getNamedRelatedObjectId = ({ type, name, parentUuid }: GetRelatedObjectIdInput) => {
  const Id = v5(`${projectCode}-${type}-${name}`, parentUuid)
  debug && console.log(`Named Item ${name}, With parentUuid: ${parentUuid}, Related Object ${type} id:`, Id)
  return Id
}

type GetNamedRelatedInstanceInput = GetRelatedObjectIdInput & {
  others?: Record<string, any>
}

/** returns predictable datastore object with name and other att, for an  object of type with given name related to outV with given uuid,
 * This is name dependent, only one object  will be presen with this parent, type and name in the system, based on the sk, pk schema
 */
export const getNamedRelatedInstance = ({ type, name, parentUuid, others }: GetNamedRelatedInstanceInput) => {
  const Id = getNamedRelatedObjectId({ type, name, parentUuid })
  const instance = getZombieInstanceFromPrimaryKey(`${type}:${Id}`, { ...others, name })
  return instance
}
